import React, { useState, useEffect, useCallback, useRef } from "react";
import { Phone, Mail, ArrowRight, ExternalLink, X, Youtube, Linkedin, Instagram, Facebook, Twitter } from "lucide-react";
import { useLanguage } from '../utils/LanguageContext';

// Import all images
import rswebsitelogo from "../assets/images/rswebsitelogo.jpg";
import namoImage from "../assets/images/namo.png";
import mssImage from "../assets/images/mss.png";
import gtImage from "../assets/images/gt.png";
import sccnImage from "../assets/images/sccn.png";

// Import preview screenshots
import rswebsitePreview from "../assets/images/rajendrashendewebsitepreview.webp";
import namoPreview from "../assets/images/nobelnamopreview.webp";
import mssPreview from "../assets/images/madhavraoshendesangeetpreview.webp";
import gtfPreview from "../assets/images/greenterrefoundationpreview.webp";
import sccnPreview from "../assets/images/sccnhubpreview.webp";

const Footer = () => {
  const { language } = useLanguage();
  // State for email subscriptions
  const ovenFreshEmailRef = useRef("");
  const sccnEmailRef = useRef("");
  const [ovenFreshEmail, setOvenFreshEmail] = useState("");
  const [sccnEmail, setSccnEmail] = useState("");
  const [ovenFreshStatus, setOvenFreshStatus] = useState({ message: "", type: "" });
  const [sccnStatus, setSccnStatus] = useState({ message: "", type: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubscriptionInProgress = useRef(false);
  
  // Website links data with eye-catching one-liners
  const websiteLinks = [
    {
      id: "rswebsite",
      title: language === 'MR' ? "राजेंद्र शेंडे यांचे अधिकृत संकेतस्थळ" : "Official Website of Rajendra Shende",
      url: "https://rajendrashende.com/",
      description: language === 'MR' ? "राजेंद्र शेंडे यांचे अधिकृत संकेतस्थळ" : "Official website of Rajendra Shende",
      shortDesc: {
        EN: "🌟 Discover the complete journey and achievements of Rajendra Shende",
        MR: "🌟 राजेंद्र शेंडे यांचा संपूर्ण प्रवास आणि यशाचा शोध घ्या"
      },
      imageUrl: rswebsitelogo,
      previewUrl: rswebsitePreview,
      previewColor: "#B91C1C",
      previewPosition: "bottom"
    },
    {
      id: "nobelnamo",
      title: language === 'MR' ? "नोबेल नमो" : "Nobel Namo",
      url: "https://nobelnamo.com",
      description: language === 'MR' ? "एसडीजी लेन्सद्वारे नरेंद्र मोदी यांचे मूल्यांकन" : "Assessment of Narendra Modi through SDG Lens",
      shortDesc: {
        EN: "🔍 Analyze PM Modi's impact through UN Sustainable Development Goals",
        MR: "🔍 संयुक्त राष्ट्राच्या शाश्वत विकास उद्दिष्टांच्या दृष्टीने पंतप्रधान मोदींच्या प्रभावाचे मूल्यांकन करा"
      },
      imageUrl: namoImage,
      previewUrl: namoPreview,
      previewColor: "#B91C1C",
      previewPosition: "bottom"
    },
    {
      id: "mss",
      title: language === 'MR' ? "माधवराव शेंडे संगीत" : "Madhavrao Shende Sangeet",
      url: "https://madhavraoshendesangeet.com",
      description: language === 'MR' ? "संगीत वारसा आणि संस्कृती" : "Musical Heritage and Culture",
      shortDesc: {
        EN: "🎵 Explore the rich musical legacy of the Shende family",
        MR: "🎵 शेंडे कुटुंबाच्या समृद्ध संगीत वारशाचा शोध घ्या"
      },
      imageUrl: mssImage,
      previewUrl: mssPreview,
      previewColor: "#B91C1C",
      previewPosition: "bottom"
    },
    {
      id: "gtf",
      title: language === 'MR' ? "ग्रीन टेरे फाउंडेशन" : "Green Terre Foundation",
      url: "https://greenterrefoundation.com",
      description: language === 'MR' ? "निसर्ग संवर्धनासाठी कृती मंच" : "Action Platform for Nature Conservation",
      shortDesc: {
        EN: "🌱 Join initiatives for environmental conservation and sustainability",
        MR: "🌱 पर्यावरण संवर्धन आणि शाश्वततेसाठी उपक्रमांमध्ये सहभागी व्हा"
      },
      imageUrl: gtImage,
      previewUrl: gtfPreview,
      previewColor: "#B91C1C",
      previewPosition: "top"
    },
    {
      id: "sccn",
      title: language === 'MR' ? "स्मार्ट कॅम्पस क्लाउड नेटवर्क (SCCN)" : "Smart Campus Cloud Network (SCCN)",
      url: "https://sccnhub.com/",
      description: language === 'MR' ? "विद्यापीठ नेटवर्क" : "University Network",
      shortDesc: {
        EN: "🎓 Connect with global universities driving sustainable campus initiatives",
        MR: "🎓 शाश्वत कॅम्पस उपक्रमांना चालना देणाऱ्या जागतिक विद्यापीठांशी जोडा"
      },
      imageUrl: sccnImage,
      previewUrl: sccnPreview,
      previewColor: "#B91C1C",
      previewPosition: "top"
    },
  ];

  // Social media links data
  const socialLinks = [
    {
      id: "youtube",
      title: "YouTube",
      url: "https://www.youtube.com/@RajendraShende",
      icon: <Youtube className="w-6 h-6" />
    },
    {
      id: "facebook",
      title: "Facebook",
      url: "https://www.facebook.com/shenderajendra/",
      icon: <Facebook className="w-6 h-6" />
    },
    {
      id: "instagram",
      title: "Instagram",
      url: "https://www.instagram.com/shenderajendra/",
      icon: <Instagram className="w-6 h-6" />
    },
    {
      id: "linkedin",
      title: "LinkedIn",
      url: "https://www.linkedin.com/in/rajendrashende/",
      icon: <Linkedin className="w-6 h-6" />
    },
  {
  id: "twitter",
  title: "Twitter",
  url: "https://x.com/rajendrashende",
  icon: (
    <svg
      className="w-6 h-6"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.22 3H21L13.5 10.63L22.5 21H15.75L10.5 14.85L4.5 21H0.75L8.76 12.88L0 3H6.9L11.61 8.69L17.22 3ZM15.96 19.5H17.85L6.72 4.41H4.71L15.96 19.5Z" />
    </svg>
  )
}

  ];

  // Email input component
  const EmailInput = ({ value, onChange, placeholder }) => {
    return (
      <input
        type="email"
        required
        maxLength={254}
        placeholder={placeholder || (language === 'MR' ? "तुमचा ईमेल पत्ता" : "Your email address")}
        value={value}
        onChange={onChange}
        className="col-span-1 sm:col-span-3 px-4 py-2.5 rounded-lg border border-gray-200 
                text-base font-medium outline-none transition-all duration-300
                focus:border-red-500 focus:ring-2 focus:ring-red-100"
      />
    );
  };

  // Subscription form component
  const IsolatedSubscribeForm = ({ onSubmit, emailValue, onEmailChange, buttonText, status, isSubmitting }) => (
    <form onSubmit={onSubmit} className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mx-2">
        <EmailInput 
          value={emailValue}
          onChange={onEmailChange}
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className={`col-span-1 flex items-center justify-center gap-1.5 px-3 py-2.5 
                   bg-red-600 text-white rounded-lg text-sm font-semibold 
                   transition-all duration-300 hover:bg-red-700 hover:-translate-y-0.5
                   min-w-[120px] mx-auto sm:mx-0 whitespace-nowrap
                   ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
        >
          <span>{isSubmitting ? (language === 'MR' ? 'सबमिट करत आहे...' : 'Submitting...') : buttonText}</span>
          {!isSubmitting && <ArrowRight className="w-4 h-4 flex-shrink-0" />}
        </button>
      </div>
      {status.message && (
        <div className={`${status.type === "success" 
            ? "bg-green-100 text-green-800" 
            : "bg-red-100 text-red-800"} 
            px-4 py-2 rounded-lg mt-2 text-base font-medium`}>
          {status.message}
        </div>
      )}
    </form>
  );

  // Section title component
  const SectionTitle = ({ children }) => (
    <h3 className="text-xl font-bold text-red-600 relative inline-block pb-2
                  after:content-[''] after:absolute after:bottom-0 after:left-0 
                  after:w-1/2 after:h-0.5 after:bg-red-600 after:transition-all 
                  after:duration-300 hover:after:w-full font-marathi">
      {children}
    </h3>
  );

  // Handle email changes
  const handleOvenFreshEmailChange = (e) => {
    ovenFreshEmailRef.current = e.target.value;
    setOvenFreshEmail(e.target.value);
  };
  
  const handleSccnEmailChange = (e) => {
    sccnEmailRef.current = e.target.value;
    setSccnEmail(e.target.value);
  };

  // Subscription handling function
  const handleSubscribe = async (emailValue, type) => {
    if (isSubscriptionInProgress.current) return;
    
    isSubscriptionInProgress.current = true;
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (type === "ovenfresh") {
        setOvenFreshStatus({
          message: language === 'MR' ? "यशस्वीरित्या सदस्यता घेतली!" : "Subscription successful!",
          type: "success",
        });
        setOvenFreshEmail("");
        ovenFreshEmailRef.current = "";
      } else {
        setSccnStatus({
          message: language === 'MR' ? "यशस्वीरित्या सदस्यता घेतली!" : "Subscription successful!",
          type: "success",
        });
        setSccnEmail("");
        sccnEmailRef.current = "";
      }
    } catch (error) {
      const errorMessage = language === 'MR' ? "सदस्यता अयशस्वी. कृपया पुन्हा प्रयत्न करा." : "Subscription failed. Please try again.";
      
      if (type === "ovenfresh") {
        setOvenFreshStatus({
          message: errorMessage,
          type: "error",
        });
      } else {
        setSccnStatus({
          message: errorMessage,
          type: "error",
        });
      }
    } finally {
      setIsSubmitting(false);
      isSubscriptionInProgress.current = false;
      
      // Clear status messages after 3 seconds
      setTimeout(() => {
        if (type === "ovenfresh") {
          setOvenFreshStatus({ message: "", type: "" });
        } else {
          setSccnStatus({ message: "", type: "" });
        }
      }, 3000);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSubscribe(ovenFreshEmailRef.current, "ovenfresh");
  };

  const handleSccnSubmit = (e) => {
    e.preventDefault();
    handleSubscribe(sccnEmailRef.current, "sccn");
  };

  // Website link with preview tooltip component
  const WebsiteLinkWithPreview = ({ website }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState('right');
    
    useEffect(() => {
      if (window.innerWidth < 768) {
        setTooltipPosition('right');
      } else {
        setTooltipPosition(website.id === 'gtf' || website.id === 'sccn' ? 'left' : 'right');
      }
    }, [website.id]);
    
    return (
      <div className="relative">
        <a
          href={website.url}
          target="_blank" 
          rel="noopener noreferrer"
          className="block p-3 rounded-lg hover:bg-red-50 transition-all duration-300 group"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 perspective-1000">
              <div className="relative w-full h-full coin-flip">
                <img
                  src={website.imageUrl}
                  alt={`${website.title} logo`}
                  className="absolute inset-0 w-full h-full rounded-full shadow-md border border-gray-200 flex-shrink-0 backface-hidden"
                />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-red-600 font-bold mb-1 group-hover:text-red-700 text-base truncate font-marathi">
                {website.title}
              </h4>
              <p className="text-gray-600 text-sm font-medium line-clamp-1 font-marathi">
                {website.shortDesc[language]}
              </p>
            </div>
          </div>
        </a>
        
        {showTooltip && (
          <div 
            className={`absolute ${tooltipPosition === 'right' ? 'left-full ml-4' : 'right-full mr-4'} 
            ${website.previewPosition === 'top' ? 'bottom-0' : 'top-0'} 
            z-50 bg-white rounded-lg shadow-xl border border-gray-200 w-72 tooltip-anim`}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <div 
              className={`absolute ${tooltipPosition === 'right' ? 'left-0 -translate-x-2' : 'right-0 translate-x-2'} 
              ${website.previewPosition === 'top' ? 'bottom-4 translate-y-1/2' : 'top-4 -translate-y-1/2'}`}
            >
              <div 
                className={`w-4 h-4 bg-white ${
                  tooltipPosition === 'right' 
                    ? (website.previewPosition === 'top' ? 'rotate-45 border-l border-t' : 'rotate-45 border-l border-b')
                    : (website.previewPosition === 'top' ? 'rotate-45 border-r border-b' : 'rotate-45 border-r border-t')
                } border-gray-200`}
              ></div>
            </div>
            
            <div 
              className="px-3 py-2 rounded-t-lg text-white font-medium flex justify-between items-center"
              style={{ backgroundColor: website.previewColor }}
            >
              <span className="truncate text-sm font-marathi">{website.title}</span>
              <button 
                onClick={() => setShowTooltip(false)} 
                className="text-white hover:opacity-80"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="p-3">
              <div className="mb-3">
                <p className="text-sm text-gray-600 font-medium">
                  {website.description}
                </p>
              </div>
              
              <div className="mb-3 overflow-hidden rounded shadow-md border border-gray-100">
                <img loading="lazy" 
                  src={website.previewUrl} 
                  alt={`${website.title} preview`}
                  className="w-full h-auto"
                />
              </div>
              
              <div className="flex items-center gap-2 mb-2">
                <img loading="lazy" 
                  src={website.imageUrl} 
                  alt={website.title}
                  className="w-6 h-6 rounded-full border border-gray-200 shadow"
                />
                <div className="min-w-0">
                  <h3 className="font-bold text-xs text-red-600 truncate font-marathi">{website.title}</h3>
                  <p className="text-xs text-gray-500 truncate">{website.url.replace(/^https?:\/\//, '')}</p>
                </div>
              </div>
              
              <a 
                href={website.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 font-marathi"
              >
                {language === 'MR' ? 'संकेतस्थळ भेट द्या' : 'Visit Website'} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <footer className="relative bg-white py-16">
      {/* CSS for animations */}
      <style jsx>{`
        @keyframes coinFlip {
          0% { transform: rotateY(0deg); }
          50% { transform: rotateY(180deg); }
          100% { transform: rotateY(360deg); }
        }
        .coin-flip {
          animation: coinFlip 3s linear infinite;
          transform-style: preserve-3d;
          perspective: 1000px;
        }
        
        @keyframes fadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        .tooltip-anim {
          animation: fadeIn 0.15s ease-out forwards;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        }
      `}</style>

      {/* Wave SVG background */}
      <div className="absolute -top-20 left-0 w-full overflow-hidden">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
          <path
            fill="#B91C1C"
            fillOpacity="0.1"
            d="M0,288L48,272C96,256,192,224,288,213.3C384,203,480,213,576,229.3C672,245,768,267,864,261.3C960,256,1056,224,1152,208C1248,192,1344,192,1392,192L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
        </svg>
      </div>

      {/* Main footer content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-start">
          
          {/* What Defines Me Section */}
          <div className="flex flex-col gap-6">
            <SectionTitle>{language === 'MR' ? 'माझी ओळख' : 'About Me'}</SectionTitle>
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-red-100 h-full">
              <h2 className="text-red-700 text-3xl font-bold mb-4 font-marathi">
                {language === 'MR' ? 'राजेंद्र शेंडे' : 'Rajendra Shende'}
              </h2>
              <p className="text-red-600 italic mb-1 text-lg font-semibold leading-6 font-marathi">
                {language === 'MR' 
                  ? 'निसर्ग हे सर्वोत्तम शाळा आहे. मी तेथे शिकायला सुरुवात केली जेव्हा मला निसर्गाने प्रवेश दिला:' 
                  : 'Nature is the best school. I got admission when I started learning there:'}
              </p>
              <p className="text-gray-500 text-base font-medium leading-7 mb-3 font-marathi">
                {language === 'MR' 
                  ? 'छोटे पक्षी नैसर्गिक साहित्य वापरून त्यांचे घरटे कसे नीटनेटके बनवतात' 
                  : 'How small birds neatly build their nests using natural materials'}
              </p>
              <p className="text-gray-500 text-base font-medium leading-7 mb-3 font-marathi">
                {language === 'MR' 
                  ? 'आणि झाडांनी पंप नसताना मातीतून पाणी वर कसे नेते याचे आश्चर्य वाटते!' 
                  : 'And how trees bring water up from the soil without a pump!'}
              </p>
              <p className="text-gray-500 text-base font-medium leading-7 mb-3 font-marathi">
                {language === 'MR' 
                  ? 'IIT मध्ये प्रवेश मिळाल्यानंतरही कोणीही मला हे शिकवले नाही.' 
                  : 'Even after getting admission to IIT, no one taught me this.'}
              </p>

              <div className="flex flex-col gap-3">
                <a
                  href="tel:+918975657235"
                  className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span className="text-base font-medium">
                    +९१ ८९७५६५७२३५
                  </span>
                </a>
                <a
                  href="mailto:shende.rajendra@gmail.com"
                  className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span className="text-base font-medium">
                    shende.rajendra@gmail.com
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Other Websites Section */}
          <div className="flex flex-col gap-6">
            <SectionTitle>{language === 'MR' ? 'माझ्या इतर प्रवासांचे जाळे' : 'My Other Digital Journeys'}</SectionTitle>
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-red-100 h-full">
              <div className="flex flex-col gap-4">
                {websiteLinks.map((website) => (
                  <WebsiteLinkWithPreview key={website.id} website={website} />
                ))}
              </div>
            </div>
          </div>

          {/* Subscription Sections */}
          <div className="flex flex-col gap-6 w-full">
            {/* Ovenfresh Subscription */}
            <div className="bg-white rounded-2xl p-6 md:p-10 shadow-lg border border-red-100">
              <SectionTitle>{language === 'MR' ? 'ओव्हनफ्रेश' : 'Ovenfresh'}</SectionTitle>
              <p className="text-gray-600 text-base md:text-lg font-medium mb-4 md:mb-6 text-center md:text-left font-marathi">
                {language === 'MR' 
                  ? 'माझे नवीनतम अपडेट्स आणि बातम्या मिळविण्यासाठी साइन अप करा.' 
                  : 'Sign up to receive my latest updates and news.'}
              </p>
              <div className="flex justify-center px-4 md:px-6 lg:px-8">
                <IsolatedSubscribeForm
                  onSubmit={handleSubmit}
                  emailValue={ovenFreshEmail}
                  onEmailChange={handleOvenFreshEmailChange}
                  buttonText={language === 'MR' ? 'साइन अप करा' : 'Sign Up'}
                  status={ovenFreshStatus}
                  isSubmitting={isSubmitting}
                />
              </div>
            </div>

            {/* SCCN Updates Subscription */}
            <div className="bg-white rounded-2xl p-6 md:p-10 shadow-lg border border-red-100">
              <SectionTitle>{language === 'MR' ? 'SCCN अपडेट्स' : 'SCCN Updates'}</SectionTitle>
              <p className="text-gray-600 text-base md:text-lg font-medium mb-4 md:mb-6 text-center md:text-left font-marathi">
                {language === 'MR' 
                  ? 'विद्यापीठांच्या अद्वितीय जागतिक नेटवर्कच्या नवीनतम अपडेट्ससाठी सदस्यता घ्या.' 
                  : 'Subscribe for the latest updates from the unique global network of universities.'}
              </p>
              <div className="flex justify-center px-4 md:px-6 lg:px-8">
                <div className="w-full max-w-md">
                  <IsolatedSubscribeForm
                    onSubmit={handleSccnSubmit}
                    emailValue={sccnEmail}
                    onEmailChange={handleSccnEmailChange}
                    buttonText={language === 'MR' ? 'सदस्यता घ्या' : 'Subscribe'}
                    status={sccnStatus}
                    isSubmitting={isSubmitting}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Icons and Copyright */}
        <div className="mt-16 border-t border-gray-200 pt-8">
          <div className="flex flex-col items-center justify-center gap-6">
            <div className="flex items-center justify-center gap-6">
              {socialLinks.map((link) => (
                <a 
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 hover:text-red-800 transition-colors duration-300 hover:scale-110"
                  aria-label={link.title}
                >
                  {link.icon}
                </a>
              ))}
            </div>
            
            <p className="text-gray-500 text-sm md:text-base text-center font-medium font-marathi">
              © {new Date().getFullYear()} {language === 'MR' 
                ? 'डॉ. राजेंद्र शेंडे यांचे सर्व हक्क राखीव.' 
                : 'All rights reserved by Dr. Rajendra Shende.'}
            </p>
          </div>
        </div>
      </div>

      {/* Marathi font support */}
      <style jsx global>{`
        @font-face {
          font-family: 'Marathi';
          src: url('/fonts/NotoSansDevanagari-Regular.ttf') format('truetype');
        }
        .font-marathi {
          font-family: 'Marathi', 'Noto Sans Devanagari', sans-serif;
        }
      `}</style>
    </footer>
  );
};

export default Footer;